/* procesamiento de pagos, generacion de qr, carrito, etc */
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { api, fetchCSRFToken } from '../services/api';
import { useAuth } from '../components/context/AuthContext';
import { paraCarrito } from '../domain/atribucion/atribucion';
import { configDe, cookiesDeMeta, rastrearEvento } from '../domain/metaPixel/metaPixel';
import { calcularSubtotal, calcularComision } from '../utils/paymentCalculations';
import { informarCompra } from '../components/seguimiento/informarCompra';
import { armarItems } from '../domain/compra/carrito';

export const usePaymentProcessing = ({
  data,
  cantidad,
  tarifasSeleccionadas,
  asientosSeleccionados,
  medioPago,
  param,
  setAsientosSeleccionados,
  setCantidad,
  setMostrarFormulario,
  setOpenSecondDrawer,
  startCooldown,
  startPolling,
  stopPolling,
  resetPolling,
  codigoCanal = ""
}) => {
  const [loadingCarrito, setLoading] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [qrExpirationTime, setQrExpirationTime] = useState(5 * 60);
  const [qrExpirationInterval, setQrExpirationInterval] = useState(null);
  // carrito del pago en curso: con QR la compra se identifica por él
  const carritoActual = useRef(null);

  const { isAuthenticated, loginWithRedirect } = useAuth();

  useEffect(() => {
    let interval;

    if (qrModalOpen && qrExpirationTime > 0) {
      interval = setInterval(() => {
        setQrExpirationTime((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            toast.warning("El código QR ha expirado", {
              position: "top-right",
              autoClose: 3000,
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [qrModalOpen, qrExpirationTime]);

  const formatQRImage = (qrImage) => {
    if (!qrImage) return '';
    if (qrImage.startsWith('data:')) {
      return qrImage;
    }
    return `data:image/png;base64,${qrImage}`;
  };

  const limpiarDespuesDeCompra = () => {
    setAsientosSeleccionados({});
    setCantidad({});
    setMostrarFormulario(false);
    setOpenSecondDrawer(false);
  };

  const resetQR = () => {
    setQrData(null);
    setQrModalOpen(false);
    setMostrarFormulario(false);
    setOpenSecondDrawer(false);
    setCantidad({});
    stopPolling();
    resetPolling();
  };

  const startPaymentStatusPolling = (reference, provider = 'clickmacro') => {
    console.log(`Iniciando polling para referencia: ${reference}, provider: ${provider}`);

    startPolling(reference, provider, {
      onSuccess: (result) => {
        console.log("Pago aprobado");
        informarCompra(carritoActual.current ? { idcarrito: carritoActual.current } : reference);
        setQrModalOpen(false);
        limpiarDespuesDeCompra();
        toast.success("¡Pago aprobado! Redirigiendo...", {
          position: "top-right",
          autoClose: 2000,
        });
        setTimeout(() => {
          window.location.href = `/mis-compras`;
        }, 2000);
      },
      onRejected: (result) => {
        console.log("Pago rechazado:", result);
        toast.error("Pago rechazado", {
          position: "top-right",
          autoClose: 4000,
        });
      },
      onTimeout: () => {
        console.log("Timeout del polling - usuario canceló o no quiere seguir esperando");
        setQrModalOpen(false);
        resetQR();
        toast.warning("Operación cancelada", {
          position: "top-right",
          autoClose: 3000,
        });
      },
      onMaxAttemptsReached: () => {
        console.log("Máximo de intentos alcanzado");
        toast.warning("Tiempo de espera agotado", {
          position: "top-right",
          autoClose: 4000,
        });
      }
    });
  };

  // respuestas: las del formulario de información adicional, por `${idtarifa}-${indice}`
  const handlePagoDirecto = async (respuestas = {}) => {
    if (loadingCarrito) return;

    setButtonDisabled(true);
    setLoading(true);

    if (!isAuthenticated) {
      await loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      });
      setButtonDisabled(false);
      setLoading(false);
      return;
    }

    const items = armarItems({
      tarifas: data?.tarifas || tarifasSeleccionadas || [],
      cantidad,
      asientosSeleccionados,
      respuestas,
    });

    if (items.length === 0) {
      toast.info("Debes seleccionar al menos una tarifa para continuar.", {
        position: "top-right",
        autoClose: 4000,
      });
      setLoading(false);
      setButtonDisabled(false);
      return;
    }

    await enviarCarrito({
      idevento: parseInt(param.id, 10),
      items,
      pago_qr: false,
      medio_pago: medioPago,
    });
  };

  const enviarCarrito = async (payload) => {
    // aseguramos el estado de carga en AMBOS caminos (directo y desde el segundo drawer con preguntas)
    setButtonDisabled(true);
    setLoading(true);
    try {
      let csrfToken = sessionStorage.getItem('csrf_token');
      if (!csrfToken) {
        csrfToken = await fetchCSRFToken();
      }

      if (!csrfToken) {
        throw new Error("No se pudo obtener el token de seguridad. Por favor, intente nuevamente.");
      }

      payload.csrf_token = csrfToken;
      const atribucion = { ...(paraCarrito(payload.idevento, codigoCanal) || {}), ...cookiesDeMeta() };
      if (Object.keys(atribucion).length > 0) payload.atribucion = atribucion;

      const response = await api.carrito.crearCarrito(payload);

      if (response.status === "success") {
        rastrearEvento(payload.idevento, 'InitiateCheckout', {
          value: Math.round((calcularSubtotal(data?.tarifas, cantidad, asientosSeleccionados) + calcularComision(data?.tarifas, cantidad, asientosSeleccionados)) * 100) / 100,
          currency: configDe(payload.idevento)?.moneda || 'ARS',
          num_items: payload.items.reduce((total, item) => total + (item.cantidad || 0), 0),
          content_ids: [String(payload.idevento)],
        });
        localStorage.setItem('lastPaymentLinkCreated', Date.now().toString());
        startCooldown();
        await procesarRespuestaCarrito(response);
      } else {
        throw new Error(response.message || "Error al procesar el pago.");
      }
    } catch (error) {
      console.error('Error creando carrito:', error);

      if (error.response?.status === 409 || error.message?.toLowerCase().includes('asiento')) {
        toast.error(
          `🎫 ${error.response?.data?.message || error.message}\n\n` +
          `Esto puede suceder cuando otra persona está comprando los mismos asientos. ` +
          `Por favor, selecciona otros asientos disponibles o espera unos minutos e intenta nuevamente.`,
          {
            position: "top-right",
            autoClose: 8000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      } else if (error.response?.status === 429) {
        toast.warning("Ya has generado un link de pago recientemente. Por favor espera unos minutos.", {
          position: "top-right",
          autoClose: 6000,
        });
      } else {
        toast.error(error.response?.data?.message || error.message || "Error al procesar la solicitud", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } finally {
      setLoading(false);
      setButtonDisabled(false);
    }
  };

  const procesarRespuestaCarrito = async (response) => {
    carritoActual.current = response.data?.id || null;
    switch (medioPago) {
      case 'clickmacro-qr':
        await handleClickMacroQR(response.data.id);
        break;
      case 'pvs-qr':
        await handlePVSQR(response.data.id);
        break;
      case 'mercadopago-qr':
        await handleMercadoPagoQR(response.data.id);
        break;
      default:
        handleRegularPayment(response);
        break;
    }
  };

  const handleClickMacroQR = async (carritoId) => {
    try {
      const clickMacroResponse = await api.clickmacro.generateClickMacroQr({
        idcarrito: carritoId,
        descripcion: `Compra en evento ${param.id}`
      }, 'online');

      if (clickMacroResponse.status === "success") {
        if (!clickMacroResponse.data.reference) {
          toast.error("Error con la referencia de pago", {
            position: "top-right",
            autoClose: 4000,
          });
          return;
        }

        setQrData({
          ...clickMacroResponse.data,
          provider: 'clickmacro'
        });
        setQrExpirationTime(10 * 60);
        setQrModalOpen(true);

        startPaymentStatusPolling(clickMacroResponse.data.reference, 'clickmacro');
      } else {
        throw new Error(clickMacroResponse.message || "Error al generar QR de Click Macro");
      }
    } catch (error) {
      toast.error(`Error al generar el código QR: ${error.message}`, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handlePVSQR = async (carritoId) => {
    try {
      const pvsResponse = await api.pvs.generatePVSQr({
        idcarrito: carritoId,
        descripcion: `Compra en evento ${param.id}`
      });

      if (pvsResponse.status === "success") {
        setQrData({
          ...pvsResponse.data,
          provider: 'pvs'
        });
        setQrExpirationTime(10 * 60);
        setQrModalOpen(true);

        if (pvsResponse.data.qrId) {
          startPaymentStatusPolling(pvsResponse.data.qrId, 'pvs');
        }
      } else {
        throw new Error(pvsResponse.message || "Error al generar QR de PVS");
      }
    } catch (error) {
      toast.error(`Error al generar el código QR: ${error.message}`, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleMercadoPagoQR = async (carritoId) => {
    try {
      const qrResponse = await api.mercadopago.generateQR(carritoId);

      if (qrResponse.status === 'success') {
        setQrData({
          qrData: qrResponse.data.qr_data,
          provider: 'mercadopago',
          reference: qrResponse.data.reference
        });
        setQrExpirationTime(10 * 60);
        setQrModalOpen(true);
      } else {
        throw new Error(qrResponse.message || "Error generando QR de MercadoPago");
      }
    } catch (error) {
      toast.error(`Error al generar el código QR: ${error.message}`, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleRegularPayment = (response) => {
    const data = response.data || {};
    const requiereAprobacion = !!data.requiere_aprobacion;
    const esCompraGratuita = response.message && response.message.includes("gratuitos");
    const hayUrlPago = data.url_pago && data.url_pago !== "";

    // carrito 100% de aprobacion: no se emitieron tickets ni hay link de pago, solo quedo la solicitud pendiente
    if (requiereAprobacion && !hayUrlPago && !esCompraGratuita) {
      toast.success("Tu solicitud quedó pendiente de aprobación del organizador. Te avisaremos por email.", {
        position: "top-right",
        autoClose: 4000,
      });

      setAsientosSeleccionados({});

      setTimeout(() => {
        window.location.href = `/mis-compras`;
      }, 2000);
      return;
    }

    if (esCompraGratuita || !hayUrlPago) {
      toast.success("¡Tickets gratuitos generados correctamente! Se enviaron a tu email.", {
        position: "top-right",
        autoClose: 3000,
      });

      // carrito mixto: ademas quedaron items pendientes de aprobacion del organizador
      if (requiereAprobacion) {
        toast.info("Algunos tickets quedaron pendientes de aprobación del organizador.", {
          position: "top-right",
          autoClose: 4000,
        });
      }

      setAsientosSeleccionados({});

      setTimeout(() => {
        window.location.href = `/mis-compras`;
      }, 2000);
    } else {
      setAsientosSeleccionados({});
      window.location.href = data.url_pago;
    }
  };

  const handleCloseQrModal = () => {
    const userConfirmed = window.confirm(
      "🚨 ¡ATENCIÓN! 🚨\n\n" +
      "Esta pestaña debe permanecer ABIERTA para completar tu pago.\n\n" +
      "❌ Si cierras ahora y ya pagaste:\n" +
      "   • NO recibiremos tu pago automáticamente\n" +
      "   • Tendrás que contactar soporte para verificar tu pago\n\n" +
      "✅ Para completar tu pago:\n" +
      "   • Escanea el QR con tu app de pago\n" +
      "   • Recibes los tickets al instante\n\n" +
      "¿REALMENTE quieres cerrar y perder tu progreso?"
    );

    if (userConfirmed) {
      resetQR();
    }
  };

  return {
    loadingCarrito,
    buttonDisabled,
    qrModalOpen,
    qrData,
    qrExpirationTime,
    setQrExpirationTime,
    qrExpirationInterval,
    setQrExpirationInterval,
    handlePagoDirecto,
    handleCloseQrModal,
    formatQRImage
  };
};